
import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { DomainRegistry } from "../../../domain";
import { ReceptionEditor, ReceptionEditorPermissions } from "../../../domain/workflow/editor";
import { ICommentConflictConfirmedResult } from "../../../domain/workflow";
import { ButtonInfo } from "../../../domain/workflow/items";
import { default as ReceptionViewBaseMixin } from "./ReceptionViewBaseMixin.vue";
import { ProgressConditionKinds, ReceptionCreateKinds, FormatStatusKinds, ReceptionCommentKinds, ReceptionCommentKindsName, ReceptionEditKinds } from "../../../domain/workflow/kinds";
import { IConfiguration } from "../../../domain/configuration";
import { AsyncUtility, UrlSchemeClient } from "../../../utilities";
import { FormatReference } from "../../../domain/format";
import { UnmanagedDocumentSummaryInformation } from "../../../domain/document";
import { ReceptionContactDetail, ReceptionDetail, ReceptionCommentSet, ReceptionComment } from "../../../domain/workflow/reception";
import { User } from "../../../domain/user";

export interface ICommentReeditResult {
  isRegisterable: boolean;
  reason: string;
}

@Component({
})
export default class ReceptionEditViewMixin extends Mixins(ReceptionViewBaseMixin) {

  @Prop()
  createKind: ReceptionCreateKinds;

  @Prop()
  sourceReceptionId: number;

  @Prop()
  sourceReceptionDetailId: number;

  @Prop()
  isActiveOnly: boolean;

  @Prop({ default: false })
  isUnmanaged: boolean;

  data(): any {
    return {
      reception: null as ReceptionEditor,
      isLoading: false,
      isRegistering: false,
      isClosing: false,
      unmanagedDocumentSummaryInformations: [],
      allUser: [],
      isCommentConflicted: false,
      isNextMessageConflicted: false,
      isRestrictCommentConflicted: false,
      confirmedCommentConflict: [],
      confirmedNextMessageConflict: [],
      confirmedRestrictCommentConflict: [],
    };
  }
  reception: ReceptionEditor;
  isLoading: boolean;
  isRegistering: boolean;
  isClosing: boolean;
  unmanagedDocumentSummaryInformations: UnmanagedDocumentSummaryInformation[];
  allUser: User[];
  isCommentConflicted: boolean;
  isNextMessageConflicted: boolean;
  isRestrictCommentConflicted: boolean;
  confirmedCommentConflict: ReceptionComment[];
  confirmedNextMessageConflict: ReceptionComment[];
  confirmedRestrictCommentConflict: ReceptionComment[];

  mounted() {
    const config = this.$store.getters.configuration as IConfiguration;
    if (config.behavior.reception.editor.shouldConfirmToLeavePage) {
      // ページ遷移時に確認ダイアログを表示する.(文言は反映されないブラウザあり.)
      window.onbeforeunload = function() {
        console.warn("編集中にページ遷移しようとしてるようです。");
        return config.behavior.reception.editor.confirmMessageToLeavePage;
      };
      this.$registerCloseConfirmation(this.closeConfirmation);
    }

    this.initializeAllUser();
  }

  private async closeConfirmation(): Promise<boolean> {
    try {
      const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
        "確認",
        this.splitByReturnCode(this.$configuration.behavior.reception.editor.confirmMessageToCloseWindow),
        this.$configuration.behavior.reception.editor.closeWindowConfirmation.buttons.confirmation.name,
        this.$configuration.behavior.reception.editor.closeWindowConfirmation.buttons.cancellation.name
      );
      return (index === 0);
    } catch (e) {
      return false;
    }
  }

  private splitByReturnCode(text: string): string[] {
    if (!text) { return [""]; }
    return text.split(/\r\n|\n/);
  }

  private initializeAllUser(): void {
    const repository = DomainRegistry.user.getUserRepository();
    repository.getAllEntities()
      .then(e => this.allUser = e)
      .catch(e => {
        this.$error(
          "職員情報取得失敗",
          "職員情報を取得できませんでした。",
          e,
        );
      });
  }

  get isLoaded(): boolean {
    return !!this.reception;
  }

  get isDepartmentRequired(): boolean {
    return !this.reception || this.reception.displayInfo.isDepartmentRequired;
  }

  /**
   * エンターキー押しっぱなしで登録系のボタンをクリックされたときに大量に登録できてしまう現象あり、
   * 登録処理側とボタン側の両方で対策している。
   * 登録処理側のみの対策だと、登録後かつ画面遷移前の隙間でIEでガードしきれない場合がある
   */
  get isBusy(): boolean {
    return !!this.isRegistering;
  }

  get isQuoted(): boolean {
    return !!this.sourceReceptionId || !!this.sourceReceptionDetailId;
  }

  // ------------------------ 登録系ボタン ---------------------------------

  get permittedRegisterTemp(): boolean {
    return this.registerTempPermission ? this.registerTempPermission.isPermitted : false;
  }

  get permittedRegister(): boolean {
    return this.registerPermission ? this.registerPermission.isPermitted : false;
  }

  get permittedRegisterAndRequestSheet(): boolean {
    return this.registerAndRequestSheetPermission ? this.registerAndRequestSheetPermission.isPermitted : false;
  }

  get registerTempPermission(): ButtonInfo {
    return this.permissions ? this.permissions.registerTemporary : null;
  }

  get registerPermission(): ButtonInfo {
    return this.permissions ? this.permissions.register : null;
  }

  get registerAndRequestSheetPermission(): ButtonInfo {
    return this.permissions ? this.permissions.registerAndRequestSheet : null;
  }

  get permissions(): ReceptionEditorPermissions {
    return this.reception ? this.reception.displayInfo.permissions : null;
  }

  get isPublishReceiptEnable(): boolean {
    return this.reception ? this.reception.displayInfo.isPublishReceipt : false;
  }

  get receiptFormatOriginalIds(): number[] {
    return this.reception ? this.reception.displayInfo.receiptFormatOriginalIds : [];
  }

  get commentLabel(): string {
    return this.reception ? this.reception.displayInfo.labelSetting.commentLabelName : "";
  }

  get restrictCommentLabel(): string {
    return this.reception ? this.reception.displayInfo.labelSetting.restrictCommentLabelName : "";
  }

  get nextMessageLabel(): string {
    return this.reception ? this.reception.displayInfo.labelSetting.nextMessageLabelName : "";
  }

  get hasEditCommentPermission(): boolean {
    return this.reception.displayInfo.isCommentEditable;
  }

  get isCommentEnable(): boolean {
    return !this.isCommentInputSingle;
  }

  get isCommentInputSingle(): boolean {
    return this.reception.displayInfo.inputUnitSetting.isCommentInputSingle;
  }

  get hasEditNextMessagePermission(): boolean {
    return this.reception.displayInfo.isNextMessageEditable;
  }

  get isNextMessageEnable(): boolean {
    return !this.isNextMessageInputSingle;
  }

  get isNextMessageInputSingle(): boolean {
    return this.reception.displayInfo.inputUnitSetting.isNextMessageInputSingle;
  }

  get hasEditRestrictCommentPermission(): boolean {
    return this.reception.displayInfo.isRestrictCommentEditable;
  }

  get isRestrictCommentEnable(): boolean {
    return this.reception.displayInfo.isRestrictCommentEnable;
  }

  // ------------------------ API通信 ---------------------------------

  async createReceptionEditor(): Promise<void> {
    switch (this.createKind) {
      case ReceptionCreateKinds.Copy:
        await this.getNewQuotedReception(true);
        this.reception.sourceReceptionId = this.sourceReceptionId;
        break;
      case ReceptionCreateKinds.Quote:
        await this.getNewQuotedReception(false);
        break;
      case ReceptionCreateKinds.CopyDetail:
        await this.getNewDetailQuotedReception(true);
        this.reception.sourceReceptionDetailId = this.sourceReceptionDetailId;
        break;
      case ReceptionCreateKinds.QuoteDetail:
        await this.getNewDetailQuotedReception(false);
        break;
      default:
        await this.getNewReception();
        break;
    }
    this.detailFormatCheck();
    this.detailManagerCheck();
  }

  // 新規依頼作成
  async getNewReception(): Promise<void> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.getNewReception();
      this.setDefaultReceiptDate(result);
      this.setDefaultContact(result);
      this.isLoading = false;

      this.reception = result;
    } catch (e) {
      this.isLoading = false;
      this.showRequestErrorMessage("依頼新規作成エラー", "依頼受付の新規作成処理に失敗しました。", e);
    }
  }

  // 新規依頼作成（受付コピー・引用）
  async getNewQuotedReception(isQuote: boolean): Promise<void> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.getNewQuotedReception(this.sourceReceptionId, this.isActiveOnly, isQuote);
      this.setDefaultReceiptDate(result);

      this.reception = result;
      if (!isQuote && this.isUnmanaged) {
        await this.setUnmanagedDocumentSummaryInformations();
        await this.setAlternativeFormat(isQuote);
      }
      this.isLoading = false;
    } catch (e) {
      this.isLoading = false;
      this.showRequestErrorMessage("依頼新規作成エラー", "依頼受付の新規作成処理に失敗しました。", e);
    }
  }

  // 新規依頼作成（依頼コピー・引用）
  async getNewDetailQuotedReception(isQuote: boolean): Promise<void> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.getNewDetailQuotedReception(this.sourceReceptionDetailId, isQuote);
      this.setDefaultReceiptDate(result);

      this.reception = result;
      if (!isQuote && this.isUnmanaged) {
        await this.setUnmanagedDocumentSummaryInformation();
        await this.setAlternativeFormat(isQuote);
      }
      this.isLoading = false;
    } catch (e) {
      this.isLoading = false;
      this.showRequestErrorMessage("依頼新規作成エラー", "依頼受付の新規作成処理に失敗しました。", e);
    }
  }

  private async setAlternativeFormat(isQuote: boolean): Promise<void> {
    // 引用で、且つ、管理外文書で代替フォーマットがある場合はフォーマットを上書きする
    const repository = DomainRegistry.format.getFormatRepository();
    for (const detail of this.reception.receptionDetails) {
      const formatOriginalId = Enumerable.from(this.unmanagedDocumentSummaryInformations)
        .where(d => d.patientNo === this.reception.reception.patientInfo.patient.id && d.documentId === detail.sourceDocumentId && d.isUnmanaged && d.isQuotable)
        .select(d => d.alternativeFormatOriginalId)
        .firstOrDefault();
      if (!!formatOriginalId) {
        detail.format = await repository.getFormatReference(formatOriginalId);
      }
    }
  }

  // 依頼引用時の記載済み文書情報取得
  private async setUnmanagedDocumentSummaryInformation(): Promise<void> {
    this.unmanagedDocumentSummaryInformations = [];
    const repository = DomainRegistry.document.getDocumentRepository();
    const summary = await repository.getDocumentSummary(this.reception.reception.patientInfo.patient.id, this.reception.receptionDetails[0].sourceDocumentId);
    const unmanagedInfo = new UnmanagedDocumentSummaryInformation(
      summary.id,
      summary.patientNo,
      summary.formatOriginalId,
      summary.isUnmanaged,
      summary.isQuotable,
      summary.alternativeFormatOriginalId,
    );
    this.unmanagedDocumentSummaryInformations.push(unmanagedInfo);
  }

  // 受付引用時の記載済み文書情報取得
  private async setUnmanagedDocumentSummaryInformations(): Promise<void> {
    this.unmanagedDocumentSummaryInformations = [];
    const repository = DomainRegistry.workflow.getWorkflowRepository();
    this.unmanagedDocumentSummaryInformations = await repository.getUnmanagedDocumentSummaryInformations(this.sourceReceptionId);
  }

  // 作成されたデータのフォーマットをチェック
  private detailFormatCheck(): void {
    for (let i = 0; i < this.reception.receptionDetails.length; i++) {
      const formatStatus = this.reception.receptionDetails[i].formatStatus;
      const warningMessage = this.getFormatWarningMessage(formatStatus);
      if (warningMessage) {
        // 禁則処理が働いているのか半角スペースがIEで妙に伸びて表示されるので、ノーブレークスペースをユニコード表現で書いている
        this.showValidationMessage("入力内容", "#" + (i + 1) + "\u{00A0}" + warningMessage);
      }
    }
  }

  // 作成されたデータの対応責任者をチェック
  private detailManagerCheck(): void {
    for (let i = 0; i < this.reception.receptionDetails.length; i++) {
      const detail = this.reception.receptionDetails[i];
      if (!detail.managers || detail.managers.length < 1) { continue; }

      if (Enumerable.from(detail.managers).any(m => !this.isValidManager(m.staff))) {
        this.showValidationMessage("入力内容", "#" + (i + 1) + " 指定した対応責任者は削除されています。選択ボタンから対応責任者を変更してください。");
      }
    }
  }

  /**
   * 記載可能な時はnullを返却します
   */
  private getFormatWarningMessage(formatStatus: FormatStatusKinds): string {
    if (formatStatus === undefined || formatStatus === FormatStatusKinds.Available) {
      // 記載可能
      // TODO: formatStatusのundefinedの判定が本当に必要なのか不明（修正前がこの判定だったので残している）
      return null;
    } else if (formatStatus === FormatStatusKinds.Deleted) {
      return "指定した文書は削除されています。 選択ボタンから文書を変更してください。";
    } else if (formatStatus === FormatStatusKinds.Expired) {
      return "指定した文書は期限が切れています。選択ボタンから文書を変更してください。";
    } else {
      return "指定した文書は記載することができません。選択ボタンから文書を変更してください。";
    }
  }

  // 受取日初期値セット（新規のみ）
  private setDefaultReceiptDate(editor: ReceptionEditor): void {
    const defaultDays = editor.displayInfo.ReceiptDateDefaultDays;
    if (defaultDays !== null) {
      const defaultReceiptDate = new Date();
      defaultReceiptDate.setDate(defaultReceiptDate.getDate() + defaultDays);
      editor.reception.recipient.receiptDate = defaultReceiptDate;
    }
  }

  private setDefaultContact(editor: ReceptionEditor): void {
    const detail = new ReceptionContactDetail();
    detail.isRequired = false;
    detail.contacts = null;
    detail.contactName = null;
    editor.reception.contact.details = [detail, detail]; // 連絡先１・２用に２件セット
    this.setDefaultContactRequired(editor);
  }

  // 連絡要・不要初期値セット（新規のみ）
  private setDefaultContactRequired(editor: ReceptionEditor): void {
    const isRequired = editor.displayInfo.defaultContactRequired;
    if (isRequired !== null) {
      editor.reception.contact.details.forEach(d => d.isRequired = isRequired);
    }
  }

  // 依頼受付編集
  async getReception(receptionId: number): Promise<void> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.editReception(receptionId);
      this.isLoading = false;

      this.reception = result;
      this.detailFormatCheck();
      this.detailManagerCheck();
    } catch (e) {
      this.isLoading = false;
      this.showRequestErrorMessage("依頼受付取得エラー", "依頼受付の取得に失敗しました。", e);
    }
  }

  // 依頼受付一時保存
  async registerAsTemporary(): Promise<void> {
    console.log(this.reception);
    try {

      if (!this.inputCheck(true)) {
        return;
      }
      if (this.isBusy) {
        this.$warn("処理中チェック", "登録処理中に別の登録処理を開始することはできません。");
        return;
      }
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isRegistering = true;
      const result = await repository.tempRegisterReception(this.reception);
      this.isRegistering = false;
      if (result.success) {
        console.log("id: " + result.id);
        this.reception.reception.id = result.id;
        this.$success("依頼受付一時保存", "依頼受付の一時保存に成功しました。");
        await this.finishEditing(false, false);
      } else {
        this.$error("依頼受付一時保存エラー", result.message + "\n" + result.reason);
      }
    } catch (e) {
      this.isRegistering = false;
      this.showRequestErrorMessage("依頼受付一時保存エラー", "依頼受付の一時保存処理でエラーが発生しました。", e);
    }
  }

  // 依頼受付登録
  async register(withCreateRequestSheet: boolean): Promise<void> {
    this.registerAndReceiptPublish(withCreateRequestSheet, false);
  }

  // 依頼受付登録+ReceiptPublish起動
  async registerAndReceiptPublish(withCreateRequestSheet: boolean, publishReceipt: boolean): Promise<void> {
    try {
      if (!this.inputCheck(false)) {
        return;
      }
      if (this.isBusy) {
        this.$warn("処理中チェック", "登録処理中に別の登録処理を開始することはできません。");
        return;
      }
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isRegistering = true;
      const result = await repository.registerReception(this.reception);
      this.isRegistering = false;
      if (result.success) {
        console.log("id: " + result.id);
        this.reception.reception.id = result.id;

        if (publishReceipt) {
          if (this.receiptFormatOriginalIds.length !== 0) {
            // Assistant経由でフォーマットデータから文書データを生成して印刷（登録はしない）
            // receiptFormatOriginalIdsは複数持てるようにはしているが、現状では先頭のみ対応している
            const service = DomainRegistry.localhost.getApplicationService();
            service.publishReceiptWithSpecifiedFormat(
              this.$store.getters.user.id,
              this.$store.getters.department.id,
              this.reception.reception.patientInfo.patient.id,
              this.reception.reception.id,
              this.receiptFormatOriginalIds[0],
              false);
          } else {
            // ReceiptPublish起動
            const service = DomainRegistry.localhost.getApplicationService();
            service.launchReceiptPublish(result.id);
          }
        }
        this.$success("依頼受付登録", "依頼受付の登録に成功しました。");
        if (this.$configuration.behavior.reception.editor.shouldRegisterNewWorkflowDocumentsWhenNewReceptionRegistered) {
          this.createNewDocuments(this.reception.reception.patientInfo.patient.id, result.id);
        }
        await this.finishEditing(withCreateRequestSheet, false);
      } else {
        this.$error("依頼受付登録エラー", result.message + "\n" + result.reason);
      }
    } catch (e) {
      this.isRegistering = false;
      this.showRequestErrorMessage("依頼受付登録エラー", "依頼受付の登録処理でエラーが発生しました。", e);
    }
  }

  private createNewDocuments(patientNo: string, receptionId: number): void {
    const departmentId = this.$store.getters.department ? this.$store.getters.department.id : "";
    const url = this.$configuration.behavior.localhost.schemeUrlPrefix
      + "?action=registerNewWorkflowDocuments"
      + "&patientNo=" + patientNo
      + "&receptionId=" + receptionId
      + "&userCode=" + this.$store.getters.user.id
      + "&departmentCode=" + departmentId
      + "&token=" + this.$store.getters.token;
    const client = new UrlSchemeClient();
    client.request(url);
  }

  // 依頼詳細編集
  async getReceptionDetail(receptionDetailId: number): Promise<void> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.getReceptionDetail(receptionDetailId);
      this.isLoading = false;

      this.reception = result;
      this.detailManagerCheck();
    } catch (e) {
      this.isLoading = false;
      this.showRequestErrorMessage("依頼取得エラー", "依頼の取得に失敗しました。", e);
    }
  }

  // 依頼詳細登録
  async registerDetail(shouldNotifyRegistration: boolean = true): Promise<boolean> {
    try {
      if (!this.inputCheck(false)) {
        return false;
      }
      if (this.isBusy) {
        this.$warn("処理中チェック", "登録処理中に別の登録処理を開始することはできません。");
        return false;
      }
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isRegistering = true;
      const result = await repository.registerReceptionDetail(this.reception);
      this.isRegistering = false;

      if (result.success) {
        console.log("id: " + result.id);
        this.reception.receptionDetails[0].id = result.id;
        this.$success("依頼登録", "依頼の登録に成功しました。");
        if (shouldNotifyRegistration) {
          await this.finishEditing(false, true);
        }
        return true;
      } else {
        const receptionDetail = this.reception.receptionDetails[0];
        const commentSet = receptionDetail ? receptionDetail.commentSet : null;
        if (this.shouldConfirmCommentsConflicts(commentSet) && result.isCommentRegistrationFailure) {
          const reeditResult = await this.reeditCommentsAndRegisterDetail(receptionDetail, shouldNotifyRegistration);
          if (reeditResult.isRegisterable) { return true; }
          if (reeditResult.reason) {
            // 複数のコメントで競合していてもresult.reasonに入っているのは最初のコメントのタイプのみ
            // 各コメントを再編集してどのコメントで登録を中断したのかわかるようにTypeを置換している
            result.reason = result.reason.replace(/\(Type:.*\)/g, "(Type:" + reeditResult.reason + ")");
          }
        }
        this.$error("依頼登録エラー", result.message + "\n" + result.reason);
      }
    } catch (e) {
      this.isRegistering = false;
      this.showRequestErrorMessage("依頼登録エラー", "依頼の登録処理でエラーが発生しました。", e);
    }
    return false;
  }

  async reeditCommentsAndRegisterDetail(receptionDetail: ReceptionDetail, shouldNotifyRegistration: boolean): Promise<ICommentReeditResult> {
    const result = await this.isRegisterableComments(receptionDetail);
    if (!result.isRegisterable) { return result; }
    this.reception.receptionDetails[0] = receptionDetail;
    const registrationResult = await this.registerDetail(shouldNotifyRegistration);
    return {
      isRegisterable: registrationResult,
      reason: "",
    };
  }

  // 入力チェック
  private inputCheck(isTemporary: boolean): boolean {
    let isSuccess: boolean = true;

    // TODO

    // 患者
    if (!this.reception.reception.patientInfo.patient) {
      this.showValidationMessage("入力エラー", "患者を指定してください。");
      isSuccess = false;
    }

    // 入力部署
    if (this.reception.mode === ReceptionEditKinds.Create && !!this.reception.reception.inputSection && this.reception.reception.inputSection.isDisable) {
      this.showValidationMessage("入力エラー", "有効な入力部署を指定してください。");
      isSuccess = false;
    }

    // 文書が最低でも1つは追加されていること
    if (this.reception.receptionDetails.length === 0) {
      this.showValidationMessage("入力エラー", "文書を指定してください。");
      isSuccess = false;
    } else {
      for (let i = 0; i < this.reception.receptionDetails.length; i++) {
        const detail = this.reception.receptionDetails[i];
        // 文書が1つ以上あり、すべて空っぽの場合はスキップする（beanへの変換時に間引く）
        if (this.reception.receptionDetails.length > 1 && detail.isEmpty) { continue; }

        // 文書が指定されていること
        if (!detail.format || !detail.format.id) {
          this.showValidationMessage("入力エラー", "#" + (i + 1) + " 文書を指定してください。");
          isSuccess = false;
        }

        if (!isTemporary) {
          const isDeparmentInputSingle = this.reception.displayInfo.inputUnitSetting.isDepartmentInputSingle;
          const department = isDeparmentInputSingle ? this.reception.commonReceptionDetail.department : detail.department;
          // 診療科が指定されていること
          if (this.isDepartmentRequired && (!department || !department.id)) {
            this.showValidationMessage("入力エラー", "#" + (i + 1) + " " + this.$wording.common.department + "を指定してください。");
            isSuccess = false;
          }

          // 対応責任者
          const isManagerInputSingle = this.reception.displayInfo.inputUnitSetting.isManagerInputSingle;
          const manager = isManagerInputSingle ? this.reception.commonReceptionDetail.managers : detail.managers;
          const progressCondition = isManagerInputSingle ? this.reception.commonReceptionDetail.route.progressCondition : detail.route.progressCondition;
          if (manager.length === 0) {
            if (this.reception.displayInfo.isManagerRequired) {
              // 対応責任者必須設定ON
              this.showValidationMessage("入力エラー", "#" + (i + 1) + " 対応責任者を指定してください。");
              isSuccess = false;
            } else if (progressCondition !== ProgressConditionKinds.None) {
              // 記載設定が「なし」以外の場合は対応責任者に最低一人は追加されていること
              this.showValidationMessage("入力エラー", "#" + (i + 1) + " 記載設定ありの場合は、対応責任者を指定してください。");
              isSuccess = false;
            }
          }

          // 文書フォーマット
          const warningMessage = this.getFormatWarningMessage(detail.formatStatus);
          if (warningMessage) {
            // 禁則処理が働いているのか半角スペースがIEで妙に伸びて表示されるので、ノーブレークスペースをユニコード表現で書いている
            this.showValidationMessage("入力エラー", "#" + (i + 1) + "\u{00A0}" + warningMessage);
            isSuccess = false;
          }
        }

        // 管理外文書
        if (this.isUnmanaged) {
          // 管理外文書は文書情報選択時に選択できないようにするため、
          // 文書が変更されていない＝管理外文書と判断する
          if (!!detail.sourceDocumentId && this.isUnmanagedDocument(detail)) {
            this.showValidationMessage("入力エラー", "#" + (i + 1) + " 代替文書を指定してください。");
            isSuccess = false;
          }
        }
      }
    }

    return isSuccess;
  }

  private isValidManager(user: User): boolean {
    if (!this.allUser || (this.allUser.length < 1)) { return true; }
    return Enumerable.from(this.allUser).any(u => u.id === user.id);
  }

  private isUnmanagedDocument(detail: ReceptionDetail): boolean {
    return Enumerable.from(this.unmanagedDocumentSummaryInformations)
      .any(d => d.patientNo === this.reception.reception.patientInfo.patient.id
        && d.documentId === detail.sourceDocumentId
        && !!detail.format
        && d.formatOriginalId === detail.format.id
        && d.isUnmanaged);
  }

  // 依頼書選択・登録完了通知
  private async finishEditing(withCreateRequestSheet: boolean, isDetail: boolean): Promise<void> {
    const format = withCreateRequestSheet
      ? await this.selectRequestSheetFormat()
      : null;
    const shouldRegisterRequestSheet = this.$configuration.behavior.reception.editor.shouldRegisterNewRequestSheetWhenNewReceptionRegistered;

    if (format && !shouldRegisterRequestSheet) {
      // 依頼書作成画面に遷移
      this.navigateRequestSheetEditor(this.reception, format);
    } else {
      if (format && this.$configuration.behavior.reception.editor.shouldRegisterNewRequestSheetWhenNewReceptionRegistered) {
        // 依頼書を自動登録、印刷
        this.registerNewRequestSheet(this.reception.reception.patientInfo.patient.id, this.reception.reception.id, format.id);
      }
      const instantCloserSetting = this.reception.displayInfo.instantCloserSetting;
      if (instantCloserSetting.isEnable) {
        // 画面を自動的に閉じる
        this.isClosing = true;
        if (instantCloserSetting.shouldShowNotification) {
          await AsyncUtility.sleep(100);  // 通知の表示が重なるので少し遅らせている
          this.$info("画面自動クローズ", "画面を自動的に閉じます。");
        }
        AsyncUtility.sleep(instantCloserSetting.waitTimeAsMilliSeconds)
        .then(() => {
          this.$closeWindow(true);
        });
      } else if (isDetail) {
        // 依頼参照画面に遷移
        const route = {
          path: "ShowReceptionDetail",
          query: {
            receptionDetailId: this.reception.receptionDetails[0].id.toString(),
          }
        };
        this.$router.replace(route);
      } else {
        // 受付参照画面に遷移
        const route = {
          path: "ShowReception",
          query: {
            receptionId: this.reception.reception.id.toString(),
          }
        };
        this.$router.replace(route);
      }
    }

    // 再検索させるためにメッセージは送信する
    this.notifyMessage("receptionRegistered");
  }

  private registerNewRequestSheet(patientNo: string, receptionId: number, formatId: number): void {
    const departmentId = this.$store.getters.department ? this.$store.getters.department.id : "";
    const url = this.$configuration.behavior.localhost.schemeUrlPrefix
      + "?action=registerNewRequestSheet"
      + "&patientNo=" + patientNo
      + "&receptionId=" + receptionId
      + "&userCode=" + this.$store.getters.user.id
      + "&departmentCode=" + departmentId
      + "&formatId=" + formatId
      + "&token=" + this.$store.getters.token;
    const client = new UrlSchemeClient();
    client.request(url);
  }

  private async selectRequestSheetFormat(): Promise<FormatReference> {
    try {
      return this.$commonDialogs.format.selector.show(this.reception.requestSheetFormats);
    } catch (e) {
      this.$error(
        this.$wording.format.name + "選択失敗",
        this.$wording.format.name + "の選択中にエラーが発生しました。",
        e,
      );
    }
    return null;
  }

  private async isRegisterableComments(sourceDetail: ReceptionDetail): Promise<ICommentReeditResult> {
    this.isCommentConflicted = false;
    if (this.shouldConfirmCommentConflict(sourceDetail.commentSet.comments) && sourceDetail.editComment) {
      const latestComment = await this.getCommentByDetailIdAndCommentType(sourceDetail.id, ReceptionCommentKindsName.getTypeName(ReceptionCommentKinds.Comment));
      const result = await this.reeditComment(sourceDetail.editComment, sourceDetail.commentSet.comments, latestComment, this.confirmedCommentConflict, this.commentLabel);
      this.isCommentConflicted = !result.isRegisterable;
      if (result.confirmed) {
        sourceDetail.editComment = result.confirmed.editComment;
        sourceDetail.commentSet.comments = result.confirmed.comments;
        this.confirmedCommentConflict.push(result.confirmed.conflicted);
      }
      if (!result.isRegisterable) {
        return {
          isRegisterable: false,
          reason: this.commentLabel,
        };
      }
    }

    this.isRestrictCommentConflicted = false;
    if (this.shouldConfirmRestrictCommentConflict(sourceDetail.commentSet.restrictComments) && sourceDetail.editRestrictComment) {
      const latestComment = await this.getCommentByDetailIdAndCommentType(sourceDetail.id, ReceptionCommentKindsName.getTypeName(ReceptionCommentKinds.Restrict));
      const result = await this.reeditComment(sourceDetail.editRestrictComment, sourceDetail.commentSet.restrictComments, latestComment, this.confirmedRestrictCommentConflict, this.restrictCommentLabel);
      this.isRestrictCommentConflicted = !result.isRegisterable;
      if (result.confirmed) {
        sourceDetail.editRestrictComment = result.confirmed.editComment;
        sourceDetail.commentSet.restrictComments = result.confirmed.comments;
        this.confirmedRestrictCommentConflict.push(result.confirmed.conflicted);
      }
      if (!result.isRegisterable) {
        return {
          isRegisterable: false,
          reason: this.restrictCommentLabel,
        };
      }
    }

    this.isNextMessageConflicted = false;
    if (this.shouldConfirmNextMessageConflict(sourceDetail.commentSet.nextMessages) && sourceDetail.editNextMessage) {
      const latestComment = await this.getCommentByDetailIdAndCommentType(sourceDetail.id, ReceptionCommentKindsName.getTypeName(ReceptionCommentKinds.NextMessage));
      const result = await this.reeditComment(sourceDetail.editNextMessage, sourceDetail.commentSet.nextMessages, latestComment, this.confirmedNextMessageConflict, this.nextMessageLabel);
      this.isNextMessageConflicted = !result.isRegisterable;
      if (result.confirmed) {
        sourceDetail.editNextMessage = result.confirmed.editComment;
        sourceDetail.commentSet.nextMessages = result.confirmed.comments;
        this.confirmedNextMessageConflict.push(result.confirmed.conflicted);
      }
      if (!result.isRegisterable) {
        return {
          isRegisterable: false,
          reason: this.nextMessageLabel,
        };
      }
    }

    return {
      isRegisterable: true,
      reason: "",
    };
  }

  async getCommentByDetailIdAndCommentType(detailId: number, type: string): Promise<ReceptionComment> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      const result = await repository.getCommentByDetailIdAndCommentType(detailId, type);
      return result;
    } catch (e) {
      this.$error("最新コメント取得失敗", "最新のコメント取得に失敗しました。", e);
      throw e;
    }
  }

  private async reeditConflictedComment(sourceComment: string, latestComment: ReceptionComment, label: string): Promise<string> {
    return this.$commonDialogs.workflow.conflictCommentComparer.show(label + "編集", label, sourceComment, latestComment);
  }

  private async reeditComment(editComment: ReceptionComment, originalComments: ReceptionComment[], latestComment: ReceptionComment, conflictConfirmedComments: ReceptionComment[], label: string): Promise<ICommentConflictConfirmedResult> {
    // 1つ目のコメントを再編集する（管理単位が単一のときしか編集ができないので２つ目以降は考慮しない）
    const content = this.toFirstReceptionCommentContent(originalComments);
    const updateDateTime = this.toFirstReceptionCommentUpdateDateTime(originalComments);
    const isConflicted = (latestComment && latestComment.type) // コメントが無いのときはnullではなくnew ReceptionComment()のためtypeもチェックしている
                      && (!updateDateTime || (updateDateTime < latestComment.updateDateTime))
                      && !conflictConfirmedComments.some(c => this.areCommentsEqual(c, latestComment));
    if (!isConflicted) {
      return {
        isRegisterable: true,
      };
    }
    const editedContent = await this.reeditConflictedComment(content, latestComment, label);
    if (editedContent === undefined) {
      return {
        isRegisterable: false,
      };
    }
    const conflictedEditContent = editComment;
    conflictedEditContent.content = editedContent;
    // 登録前にCommentIdとArchiveIdで競合をチェックしているため更新
    conflictedEditContent.id = latestComment.deleted ? null : latestComment.id;
    conflictedEditContent.archiveId = latestComment.deleted ? null : latestComment.archiveId;
    return {
      isRegisterable: true,
      confirmed: {
        editComment: conflictedEditContent,
        comments: new Array(conflictedEditContent),
        conflicted: latestComment,
      }
    };
  }

  private toFirstReceptionComment(comments: ReceptionComment[]): ReceptionComment {
    return (comments && (0 < comments.length))
      ? comments[0]
      : null;
  }

  private toFirstReceptionCommentContent(sources: ReceptionComment[]): string {
    return this.toFirstReceptionComment(sources)
      ? this.toFirstReceptionComment(sources).content
      : "";
  }

  private toFirstReceptionCommentUpdateDateTime(sources: ReceptionComment[]): Date {
    return this.toFirstReceptionComment(sources)
      ? this.toFirstReceptionComment(sources).updateDateTime
      : null;
  }

  private shouldConfirmCommentsConflicts(commentSet: ReceptionCommentSet): boolean {
    return (this.shouldConfirmCommentConflict(commentSet.comments)
        || this.shouldConfirmNextMessageConflict(commentSet.nextMessages)
        || this.shouldConfirmRestrictCommentConflict(commentSet.restrictComments));
  }

  private shouldConfirmCommentConflict(comments: ReceptionComment[]): boolean {
    return this.hasEditCommentPermission && this.isCommentEnable && this.hasEnteredCommentEditable(comments);
  }

  private shouldConfirmNextMessageConflict(comments: ReceptionComment[]): boolean {
    return this.hasEditNextMessagePermission && this.isNextMessageEnable && this.hasEnteredCommentEditable(comments);
  }

  private shouldConfirmRestrictCommentConflict(comments: ReceptionComment[]): boolean {
    return this.hasEditRestrictCommentPermission && this.isRestrictCommentEnable && this.hasEnteredCommentEditable(comments);
  }

  private hasEnteredCommentEditable(comments: ReceptionComment[]): boolean {
    return comments.length === 0 || (comments.length === 1 && comments[0].isEditable);
  }

  onConflictResolved(kind: ReceptionCommentKinds, confirmedComment: ReceptionComment): void {
    switch (kind) {
      case ReceptionCommentKinds.Comment:
        if (!this.confirmedCommentConflict.some(c => this.areCommentsEqual(c, confirmedComment))) {
          this.confirmedCommentConflict.push(confirmedComment);
        }
        break;
      case ReceptionCommentKinds.Restrict:
        if (!this.confirmedRestrictCommentConflict.some(c => this.areCommentsEqual(c, confirmedComment))) {
          this.confirmedRestrictCommentConflict.push(confirmedComment);
        }
        break;
      case ReceptionCommentKinds.NextMessage:
        if (!this.confirmedNextMessageConflict.some(c => this.areCommentsEqual(c, confirmedComment))) {
          this.confirmedNextMessageConflict.push(confirmedComment);
        }
        break;
    }
  }

  private areCommentsEqual(source: ReceptionComment, destination: ReceptionComment): boolean {
    return source.id === destination.id
      && source.type === destination.type
      && source.content === destination.content
      && (source && destination && source.updateDateTime.getTime() === destination.updateDateTime.getTime());
  }
}
